/**
 * Active org's COHORTS (F2-A): listing + creation (admin/teacher). Data
 * arrives via the role-based streams (org_admin_scope / campus_admin_scope /
 * my_cohorts_teacher); creating goes through the write-path (ManageAcademics authz with
 * campus scoping — an unauthorized attempt leaves no trace).
 */
import { type Component, createSignal, For, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { CalendarRange, Plus, Users } from "@fleet/icons";
import {
  createCohortMutation,
  DEFAULT_COHORT_MODALITY,
  MODALITY_IN_PERSON_KEY,
} from "@space/shared";
import { Link } from "@tanstack/solid-router";
import { useI18n } from "@/providers/i18n-provider";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@fleet/ui";
import { showToast } from "@/components/ui/toast";
import { useDrizzle } from "@/hooks/use-drizzle";
import { listEntranceProps, useListEntrance } from "@/lib/animation/list-entrance";
import { useActiveOrg } from "../hooks/use-active-org";
import { useCampuses } from "../hooks/use-academy-data";
import { useCohortsByOrg, useOrgCourses } from "../hooks/use-cohort-data";
import { asModality, type Modality, useModalityOptions } from "../hooks/use-modality-options";
import { useFeatureEnabled } from "@/features/flags/use-feature-enabled";

const field =
  "w-full rounded-lg border-2 border-border bg-field-background px-3 py-2 text-sm focus-visible:border-primary focus-visible:outline-none";

type Rule = "open" | "approval" | "waitlist";
const asRule = (v: string): Rule => (v === "approval" || v === "waitlist" ? v : "open");

export const CohortsView: Component = () => {
  const { t } = useI18n();
  const drizzle = useDrizzle();
  const { activeOrgId, isLoading: orgLoading } = useActiveOrg();
  const cohorts = useCohortsByOrg(activeOrgId);
  const courses = useOrgCourses(activeOrgId);
  const campuses = useCampuses(activeOrgId);

  const isEntering = useListEntrance({
    scope: () => `cohorts:${activeOrgId() ?? ""}`,
    hasItems: () => (cohorts.data ?? []).length > 0,
  });

  const [showForm, setShowForm] = createSignal(false);
  const [name, setName] = createSignal("");
  const [courseId, setCourseId] = createSignal("");
  const [campusId, setCampusId] = createSignal("");
  // §L2: online live by default — the academy is online-only.
  const [modality, setModality] = createSignal<Modality>(DEFAULT_COHORT_MODALITY);
  const modalityOptions = useModalityOptions({ orgId: activeOrgId });
  // §L1: the campus is only chosen if the academy uses in-person.
  const inPerson = useFeatureEnabled(MODALITY_IN_PERSON_KEY, { orgId: activeOrgId });
  const [rule, setRule] = createSignal<Rule>("open");
  const [capacity, setCapacity] = createSignal(0);

  const createCohort = async () => {
    const db = drizzle();
    const org = activeOrgId();
    if (!db || !org || !courseId()) {
      return;
    }
    await createCohortMutation(db, {
      organizationId: org,
      courseId: courseId(),
      campusId: campusId() || null,
      name: name(),
      modality: modality(),
      enrollmentRule: rule(),
      capacity: capacity(),
      status: "open",
    });
    setShowForm(false);
    setName("");
    showToast(t(msg`Cohort created`), "success");
  };

  return (
    <div class="mx-auto max-w-3xl space-y-6 p-4 md:p-6">
      <div class="flex items-center justify-between">
        <h1 class="font-display text-2xl font-extrabold tracking-tight" data-testid="cohorts-title">
          {t(msg`Cohorts`)}
        </h1>
        <div class="flex items-center gap-1">
          <Button data-testid="new-cohort" onClick={() => setShowForm((s) => !s)} class="gap-2">
            <Plus class="h-4 w-4" /> {t(msg`New cohort`)}
          </Button>
        </div>
      </div>
      {/* AUTONOMY · Layer 2: the screen's visible purpose. */}
      <p class="text-sm text-muted-foreground">
        {t(
          msg`A cohort is a scheduled edition of a course: it defines modality, campus, capacity, schedule and enrollment rule. Students enroll in cohorts, not in courses.`,
        )}
      </p>

      <Show when={showForm()}>
        <div class="space-y-3 rounded-2xl border-2 border-border p-5" data-testid="cohort-form">
          <input
            class={field}
            data-testid="cohort-name"
            value={name()}
            onInput={(e) => setName(e.currentTarget.value)}
            placeholder={t(msg`Cohort name (e.g. AI 2026-1)`)}
          />
          <select
            class={field}
            data-testid="cohort-course"
            value={courseId()}
            onChange={(e) => setCourseId(e.currentTarget.value)}
          >
            <option value="">{t(msg`Choose a course…`)}</option>
            <For each={courses.data ?? []}>{(c) => <option value={c.id}>{c.title}</option>}</For>
          </select>
          <div class="grid grid-cols-2 gap-3">
            <select
              class={field}
              data-testid="cohort-modality"
              value={modality()}
              onChange={(e) => setModality(asModality(e.currentTarget.value))}
            >
              <For each={modalityOptions()}>
                {(o) => <option value={o.value}>{t(o.label)}</option>}
              </For>
            </select>
            <select
              class={field}
              data-testid="cohort-rule"
              value={rule()}
              onChange={(e) => setRule(asRule(e.currentTarget.value))}
            >
              <option value="open">{t(msg`Open enrollment`)}</option>
              <option value="approval">{t(msg`Requires approval`)}</option>
              <option value="waitlist">{t(msg`Waitlist`)}</option>
            </select>
          </div>
          <div class="grid grid-cols-2 gap-3">
            <Show when={inPerson().enabled}>
              <select
                class={field}
                data-testid="cohort-campus"
                value={campusId()}
                onChange={(e) => setCampusId(e.currentTarget.value)}
              >
                <option value="">{t(msg`All campuses`)}</option>
                <For each={campuses.data ?? []}>
                  {(c) => <option value={c.id}>{c.name}</option>}
                </For>
              </select>
            </Show>
            <input
              type="number"
              min="0"
              class={field}
              data-testid="cohort-capacity"
              value={capacity()}
              onInput={(e) => setCapacity(Number(e.currentTarget.value) || 0)}
              placeholder={t(msg`Capacity (0 = unlimited)`)}
            />
          </div>
          <Button data-testid="cohort-create" onClick={() => void createCohort()}>
            {t(msg`Create cohort`)}
          </Button>
        </div>
      </Show>

      <Show when={!orgLoading()} fallback={<Skeleton class="h-40 w-full rounded-2xl" />}>
        <Show
          when={(cohorts.data ?? []).length > 0}
          fallback={
            <p class="rounded-2xl border-2 border-dashed border-border p-8 text-center text-muted-foreground">
              {/* AUTONOMY · Layer 2: empty state that teaches the concept. */}
              {t(
                msg`No cohorts yet. A cohort is a scheduled edition of a course: it defines modality, campus, capacity and schedule. Use "New cohort" to create the first one.`,
              )}
            </p>
          }
        >
          <ul class="space-y-2" data-testid="cohorts-list">
            <For each={cohorts.data ?? []}>
              {(c, index) => {
                const entrance = () => listEntranceProps(isEntering(), index());
                return (
                  <li class={entrance().class} style={entrance().style}>
                    <Link
                      to="/academy/cohorts/$cohortId"
                      params={{ cohortId: c.id }}
                      data-testid="cohort-item"
                      class="flex items-center justify-between rounded-2xl border border-border p-4 transition-[color,background-color,border-color,transform] duration-150 ease-out hover:border-primary/50 active:scale-[0.99]"
                    >
                      <div>
                        <p class="font-semibold" data-testid="cohort-item-name">
                          {c.name}
                        </p>
                        <p class="mt-0.5 flex items-center gap-2 text-xs text-muted-foreground">
                          <CalendarRange class="h-3.5 w-3.5" />
                          {c.modality} · {c.enrollment_rule}
                          <Show when={(c.capacity ?? 0) > 0}>
                            <span class="inline-flex items-center gap-1">
                              <Users class="h-3.5 w-3.5" /> {c.capacity}
                            </span>
                          </Show>
                        </p>
                      </div>
                      <span
                        class="rounded-full bg-accent/12 px-3 py-1 text-xs font-bold text-accent"
                        data-testid="cohort-item-status"
                      >
                        {c.status}
                      </span>
                    </Link>
                  </li>
                );
              }}
            </For>
          </ul>
        </Show>
      </Show>
    </div>
  );
};
